/**
 * Leitura dos tokens de cor de `tokens.css`.
 *
 * Funcoes PURAS: recebem o texto do CSS e devolvem numeros. Nao tocam em disco
 * nem no DOM, por isso servem tanto ao navegador (o texto vem de
 * `tokens.css?raw`) quanto ao script de auditoria de contraste, que roda no
 * Node (o texto vem de `readFileSync`). `tokens.css` continua sendo a unica
 * fonte da verdade das cores.
 */

/** Cor de um token como esta escrita em tokens.css: matiz (graus) e saturacao/luminosidade (%). */
export interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** RGB com cada canal em 0..1. */
export type Rgb = [number, number, number];

/** Extrai os tokens de um bloco de seletor (`:root`, `.dark`, alto contraste). */
export function lerBloco(css: string, seletor: string): Map<string, Hsl> {
  const inicio = css.indexOf(seletor);
  if (inicio === -1) {
    throw new Error(`Bloco ${seletor} nao encontrado em tokens.css`);
  }
  const abre = css.indexOf('{', inicio);
  const fecha = css.indexOf('}', abre);
  const corpo = css.slice(abre + 1, fecha);

  const tokens = new Map<string, Hsl>();
  const regex = /--([a-z][a-z0-9-]*):\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/g;
  let achado: RegExpExecArray | null;
  while ((achado = regex.exec(corpo)) !== null) {
    tokens.set(achado[1]!, {
      h: Number(achado[2]),
      s: Number(achado[3]),
      l: Number(achado[4]),
    });
  }
  return tokens;
}

/** HSL -> RGB, cada canal em 0..1. */
export function paraRgb({ h, s, l }: Hsl): Rgb {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return [r + m, g + m, b + m];
}

/** HSL -> RGB com cada canal inteiro em 0..255 (o formato que o jsPDF espera). */
export function paraRgb255(cor: Hsl): Rgb {
  const [r, g, b] = paraRgb(cor);
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
